import { useState } from "react";

import { PlaygroundCredentialMode } from "../types";
import { LocalStorageKeys, useLocalStorage } from "../util/localStorage";

export type PlaygroundCredentials = {
  mode: PlaygroundCredentialMode;
  apiKeyId: string;
  m2mClientId: string;
  m2mClientSecret: string;
};

// What survives a browser restart. The M2M client secret is deliberately
// absent: it lives in sessionStorage instead, so it is never written to
// disk in plaintext. Everything here is a non-secret selection.
type PersistentPlaygroundCredentials = Omit<
  PlaygroundCredentials,
  "m2mClientSecret"
>;

const DEFAULT_CREDENTIALS: PlaygroundCredentials = {
  mode: "sso",
  apiKeyId: "",
  m2mClientId: "",
  m2mClientSecret: "",
};

// The developer's Playground credential choice, persisted per user/org
// (BAGEL-414), so the Playground never asks for credentials again.
//
// Split by sensitivity: the mode, API key id, and M2M client id go to
// localStorage, so the choice survives a restart. The M2M client secret
// goes to sessionStorage, so it survives the remount and a reload in the
// same tab but is dropped when the tab closes and never lands on disk.
// After a restart an M2M user returns in M2M mode with their client id
// prefilled and re-enters only the secret.
export const usePlaygroundCredentials = (
  supportedModes: readonly PlaygroundCredentialMode[]
): {
  credentials: PlaygroundCredentials;
  setCredentials: (update: Partial<PlaygroundCredentials>) => void;
  isReplayReady: boolean;
} => {
  const storage = useLocalStorage<PersistentPlaygroundCredentials>();
  const secretStorage = useLocalStorage<string>({ sessionStorage: true });

  const [credentials, setCredentialsState] = useState<PlaygroundCredentials>(
    () => {
      const persisted = storage.getItem(
        LocalStorageKeys.PLAYGROUND_CREDENTIALS
      );
      const merged = {
        ...DEFAULT_CREDENTIALS,
        ...persisted,
        m2mClientSecret:
          secretStorage.getItem(LocalStorageKeys.PLAYGROUND_M2M_SECRET) ?? "",
      };
      // A persisted mode this app does not support (e.g. "sso" persisted
      // from the Dashboard, read in Admin) falls back to the app's first
      // supported mode.
      return supportedModes.includes(merged.mode)
        ? merged
        : { ...merged, mode: supportedModes[0] ?? "api_key" };
    }
  );

  const setCredentials = (update: Partial<PlaygroundCredentials>) => {
    setCredentialsState((previous) => {
      const next = { ...previous, ...update };
      const { m2mClientSecret, ...persistable } = next;
      storage.setItem(LocalStorageKeys.PLAYGROUND_CREDENTIALS, persistable);
      secretStorage.setItem(
        LocalStorageKeys.PLAYGROUND_M2M_SECRET,
        m2mClientSecret
      );
      return next;
    });
  };

  const isReplayReady =
    credentials.mode === "sso" ||
    (credentials.mode === "m2m"
      ? !!(credentials.m2mClientId && credentials.m2mClientSecret)
      : !!credentials.apiKeyId);

  return { credentials, setCredentials, isReplayReady };
};
